import React from 'react';

import QRCode from 'qrcode';

import { getPitstopById } from '@/services/api/pitstops';

import { type RequestOptions, warehouseApi } from '../api/warehouse.api';
import { fmtDateTimeLong } from '../format';
import { poSourceOf, poTypeOf } from '../status';
import DeliveryNotePdf, { deliveryNoteRows } from './DeliveryNotePdf';
import GoodsReceiptPdf from './GoodsReceiptPdf';
import PickingListPdf from './PickingListPdf';
import type { PrintMeta } from './pdfKit';
import PurchaseOrderPdf from './PurchaseOrderPdf';

export type PrintKind = 'picking' | 'sj' | 'gr' | 'po';

export const PRINT_TITLE: Record<PrintKind, string> = {
  picking: 'Picking list',
  sj: 'Surat jalan',
  gr: 'Goods receipt',
  po: 'Purchase order',
};

export interface PrintDocument {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  element: React.ReactElement<any>;
  /** Document number (QR content + filename stem). */
  code: string;
  filename: string;
}

const qrOf = async (text: string): Promise<string | null> => {
  try {
    return await QRCode.toDataURL(text, {
      errorCorrectionLevel: 'M',
      margin: 0,
      width: 240,
    });
  } catch {
    return null;
  }
};

const hubAddressOf = async (hubID: number): Promise<string | null> => {
  try {
    return (await getPitstopById(hubID)).address ?? null;
  } catch {
    return null;
  }
};

const safeFile = (s: string) => s.replace(/[^A-Za-z0-9._-]+/g, '-');

/**
 * Load the detail behind one print document and assemble its PDF element.
 * Data comes from the regular detail endpoints (TRD: no print endpoint).
 * `signal` aborts those requests (the preview closed or switched document).
 */
export const buildPrintDocument = async (
  kind: PrintKind,
  id: string,
  printedBy: string,
  signal?: AbortSignal
): Promise<PrintDocument> => {
  const o: [] | [RequestOptions] = signal ? [{ signal }] : [];
  const meta = async (code: string): Promise<PrintMeta> => ({
    qr: await qrOf(code),
    printedAt: fmtDateTimeLong(new Date().toISOString()),
    printedBy,
  });

  if (kind === 'picking') {
    const detail = await warehouseApi.getGoodsIssue(id, ...o);
    const code = detail.goodsIssue.code;
    return {
      element: <PickingListPdf detail={detail} meta={await meta(code)} />,
      code,
      filename: `picking-list-${safeFile(code)}.pdf`,
    };
  }

  if (kind === 'sj') {
    const detail = await warehouseApi.getGoodsIssue(id, ...o);
    const sj = detail.goodsIssue.suratJalanNo;
    if (!sj) {
      throw new Error(
        'Nomor surat jalan belum terbit — selesaikan picking lalu Ready to pick up.'
      );
    }
    // Per-unit weights come with the GI lines (from the SKU catalog).
    const hubAddress = await hubAddressOf(detail.goodsIssue.hubID);
    return {
      element: (
        <DeliveryNotePdf
          detail={detail}
          rows={deliveryNoteRows(detail)}
          hubAddress={hubAddress}
          meta={await meta(sj)}
        />
      ),
      code: sj,
      filename: `surat-jalan-${safeFile(sj)}.pdf`,
    };
  }

  if (kind === 'gr') {
    const detail = await warehouseApi.getGoodsReceipt(id, ...o);
    const code = detail.receipt.code;
    // The PO tells the type (vendor / transfer / return); print without it if it fails.
    const poID = detail.receipt.purchaseOrderID;
    const po = poID
      ? await warehouseApi
          .getPurchaseOrder(poID, ...o)
          .then((d) => d?.order ?? null)
          .catch(() => null)
      : null;
    return {
      element: (
        <GoodsReceiptPdf
          detail={detail}
          meta={await meta(code)}
          poType={po ? poTypeOf(po) : null}
          source={po ? poSourceOf(po) : null}
        />
      ),
      code,
      filename: `goods-receipt-${safeFile(code)}.pdf`,
    };
  }

  const detail = await warehouseApi.getPurchaseOrder(id, ...o);
  if (detail.order.status === 'CANCELLED') {
    throw new Error('PO yang dibatalkan tidak bisa dicetak.');
  }
  const code = detail.order.code;
  return {
    element: (
      <PurchaseOrderPdf
        detail={detail}
        hubAddress={await hubAddressOf(detail.order.hubID)}
        meta={await meta(code)}
      />
    ),
    code,
    filename: `purchase-order-${safeFile(code)}.pdf`,
  };
};
